import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { companyInfo } from '../../data/company';
import { submitWeb3Form } from '../../services/web3Forms';
import { Badge, Card, Input, Textarea, Button } from '../ui';

export interface ContactSectionProps {
  onOpenConsultation?: () => void;
}

export const ContactSection: React.FC<ContactSectionProps> = ({ onOpenConsultation }) => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitWeb3Form('rfp', {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message,
        subject: `New Technook RFP Inquiry from ${formData.fullName}`,
      });
    } catch (err) {
      console.log('Web3Forms submission handled:', err);
    } finally {
      setIsSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-16 lg:py-20 relative bg-[#f5f9fc] dark:bg-[#080e1a] transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-8 sm:mb-12 text-left space-y-3">
          <Badge variant="brand" className="px-3.5 py-1 text-[11px] sm:text-xs uppercase tracking-wider">
            GET IN TOUCH
          </Badge>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#184166] dark:text-white tracking-tight">
            Discuss your project with our Parañaque team.
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
            Ready to transform your technical systems or launch your next mobile app? Connect with our lead software architects directly or send an instant RFP inquiry.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Contact Information Cards matching website-ui-ux */}
          <div className="lg:col-span-5 space-y-4 sm:space-y-6">
            
            <Card variant="glass" className="p-6 bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 space-y-4 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-sky-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-[#4a82c2] shrink-0">
                  <MapPin size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-[#184166] dark:text-white">Technook Philippines Office</h4>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    Imperial Plaza, Diosdado Macapagal Blvd., Tambo, Parañaque City, Metro Manila
                  </p>
                </div>
              </div>
            </Card>

            <Card variant="glass" className="p-6 bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 space-y-4 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-teal-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-[#08adae] shrink-0">
                  <Mail size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-[#184166] dark:text-white">Email Us Directly</h4>
                  <a href={`mailto:${companyInfo.email}`} className="text-xs font-mono font-bold text-[#4a82c2] hover:underline block">
                    {companyInfo.email}
                  </a>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Response SLA within 4 business hours</p>
                </div>
              </div>
            </Card>

            <Card variant="glass" className="p-6 bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 space-y-4 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-emerald-600 dark:text-emerald-400 shrink-0">
                  <Phone size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-[#184166] dark:text-white">Direct Phone & WhatsApp</h4>
                  <p className="text-xs font-mono text-slate-700 dark:text-slate-300 font-semibold">
                    {companyInfo.phones.join(' | ')}
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Viber & WhatsApp Instant Messaging Available</p>
                </div>
              </div>
            </Card>

            {/* Strategy Call Banner */}
            <Card variant="glass" className="p-5 bg-gradient-to-r from-sky-50 to-teal-50 dark:from-[#0b1322] dark:to-[#0f1b2d] border border-sky-200 dark:border-slate-800 flex items-center justify-between gap-4">
              <div className="space-y-1">
                <span className="text-xs font-bold text-[#184166] dark:text-white flex items-center gap-1.5">
                  <Clock size={14} className="text-[#08adae]" /> Prefer a live 1-on-1 strategy call?
                </span>
                <p className="text-[11px] text-slate-600 dark:text-slate-400">Book an appointment directly with our lead software architects.</p>
              </div>
              <Button variant="primary" size="sm" onClick={onOpenConsultation} className="shrink-0">
                <span>Book Strategy Call</span>
              </Button>
            </Card>

          </div>

          {/* Right Direct RFP Form */}
          <div className="lg:col-span-7">
            <Card variant="glass" className="p-6 sm:p-10 bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 shadow-2xl space-y-6">
              
              <div className="border-b border-slate-200 dark:border-slate-800 pb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-[#184166] dark:text-white">Send RFP Inquiry</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Fill out your project details for an immediate technical scope review.</p>
                </div>
                <Badge variant="accent" className="text-[10px]">
                  RFP Fast-Track
                </Badge>
              </div>

              {submitted ? (
                <div className="p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-center space-y-3 py-10">
                  <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
                  <h4 className="text-lg font-bold text-[#184166] dark:text-white">RFP Inquiry Received!</h4>
                  <p className="text-xs text-slate-600 dark:text-slate-300 max-w-md mx-auto">
                    Thank you for reaching out to Technook Global. Our lead engineering team at Imperial Plaza, Parañaque City will review your project scope and respond within 4 business hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Full Name"
                      placeholder="e.g. Juan Cruz"
                      required
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    />
                    <Input
                      label="Email Address"
                      type="email"
                      placeholder="name@company.com"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Phone / Viber Number"
                      placeholder="+63 917 123 4567"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                    <Input
                      label="Company Name"
                      placeholder="e.g. Acme Enterprises"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    />
                  </div>

                  <Textarea
                    label="Project Scope & Technical Requirements"
                    placeholder="Tell us about your project, target features, delivery timeline, or questions..."
                    rows={4}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />

                  <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    <span>{isSubmitting ? 'Sending RFP...' : 'Submit RFP Scope Request'}</span>
                  </Button>
                </form>
              )}

            </Card>
          </div>

        </div>
      </div>
    </section>
  );
};
